import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { LandingPageConfig } from '../types';
import { apiService } from '../services/api';
import LandingPageTemplate from './LandingPageTemplate';
import LandingPageTemplateSettled from './LandingPageTemplateSettled';
import { themes } from '../themes';

// Asset preloading function
const preloadAssets = async (config: LandingPageConfig): Promise<void> => {
  const promises: Promise<void>[] = [];

  // Preload profile image
  if (config.imageUrl) {
    promises.push(new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = () => resolve(); // Don't fail on image load errors
      img.src = config.imageUrl;
    }));
  }

  // Preload fallback image
  if (config.fallback?.imageUrl) {
    promises.push(new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = () => resolve();
      img.src = config.fallback.imageUrl;
    }));
  }

  await Promise.all(promises);
};

// Initial unthemed loader - plain white background, black text
const InitialLoader: React.FC = () => (
  <div style={{
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#ffffff',
    color: '#000000',
    fontSize: '1.2rem',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  }}>
    <div style={{
      textAlign: 'center',
      padding: '40px',
    }}>
      <div
        style={{
          width: '50px',
          height: '50px',
          border: '4px solid #00000033',
          borderTop: '4px solid #000000',
          borderRadius: '50%',
          margin: '0 auto 20px',
          animation: 'spin 1s linear infinite',
        }}
      />
      <div>Almost there...</div>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  </div>
);

// Theme-aware loading spinner component
const LoadingSpinner: React.FC<{ theme?: string }> = ({ theme = 'soft-pinks' }) => {
  const themeConfig = themes[theme] || themes['soft-pinks'];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: themeConfig.colors.background,
        color: themeConfig.colors.text,
        fontSize: '1.2rem',
        fontFamily: themeConfig.fonts.body,
      }}
    >
      <div style={{
        textAlign: 'center',
        padding: '40px',
      }}>
        <div
          style={{
            width: '50px',
            height: '50px',
            border: `4px solid ${themeConfig.colors.button}33`,
            borderTop: `4px solid ${themeConfig.colors.button}`,
            borderRadius: '50%',
            margin: '0 auto 20px',
            animation: 'spin 1s linear infinite',
          }}
        />
        <div>Almost there...</div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </motion.div>
  );
};

const LandingPage: React.FC = () => {
  const { pageSlug } = useParams<{ pageSlug: string }>();
  const [config, setConfig] = useState<LandingPageConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [assetsLoaded, setAssetsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadPageConfig = async () => {
      try {
        const slug = pageSlug || 'my-landing-page';

        const savedConfig = await apiService.loadLandingPage(slug);

        if (savedConfig) {
          setConfig(savedConfig);
          // Preload all assets before showing the page
          await preloadAssets(savedConfig);
          setAssetsLoaded(true);
        } else {
          setError('Landing page not found');
        }
      } catch (err) {
        setError('Failed to load landing page configuration');
        console.error('Error loading page config:', err);
      } finally {
        setLoading(false);
      }
    };

    loadPageConfig();
  }, [pageSlug]);

  return (
    <AnimatePresence mode="wait">
      {error ? (
        (() => {
          const themeConfig = themes[config?.theme || 'soft-pinks'];
          return (
            <motion.div
              key="error"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              style={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: themeConfig.colors.background,
                color: themeConfig.colors.text,
                textAlign: 'center',
                padding: '32px',
                fontFamily: themeConfig.fonts.body,
              }}
            >
              <div style={{
                maxWidth: '420px',
                width: '100%',
                padding: '32px 28px',
              }}>
                <h1 style={{ margin: '0 0 12px 0', fontFamily: themeConfig.fonts.heading }}>
                  oops!
                </h1>
                <p style={{ margin: 0, lineHeight: 1.6 }}>
                  my link is broken rn, try again in 5 minutes, sorry : (
                </p>
              </div>
            </motion.div>
          );
        })()
      ) : loading && !config ? (
        <motion.div
          key="initial"
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <InitialLoader />
        </motion.div>
      ) : config && !assetsLoaded ? (
        <LoadingSpinner key="themed" theme={config.theme} />
      ) : config && assetsLoaded ? (
        <motion.div
          key="content"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          {config.buttonPosition === 'settled' ? (
            <LandingPageTemplateSettled config={config} pageId={config.id} />
          ) : (
            <LandingPageTemplate config={config} pageId={config.id} />
          )}
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};

export default LandingPage;
